<template>
  <div class="brand-selector" :class="{ 'open-selector': openSelector, loading: loading }">

    <div v-if="!selectedBrand || !hideOnSelect" class="selected-brand brand none-selected premium-badge--inline">
      <div v-if="availableBrands.length > 0" @click="toggleDropdown">
        {{ $gettext('Select trade name') }}
        <font-awesome-icon :icon="'fa-solid fa-chevron-down'" class="selector-caret" />
      </div>
      <GekkoButton
        v-else
        @click="addBrand"
        :text="$gettext('Add trade name')"
        :icon="'fa-solid fa-plus'"
        variant="link"
        class="px-0"
        size="sm"
      />
      <GkBadge 
        icon="fa-solid fa-crown"
        only-basic
        icon-only
        type="premium"
      />
    </div>

    <div class="options-list-wrap" :style="cssVars">
      <overlay-scrollbars :options="scrollBarOptions" class="selector-scrollbar">
        <div class="options-list">

          <div v-if="!hasMainBrand" key="brand_main" @click="selectMainBrand()" class="brand" :class="{ selected: !selectedId }">
            <div class="brand-logo">
              <img v-if="currentCompany.logo" :src="currentCompany.logo" :alt="currentCompany.name">
              <div v-else class="no-logo">
                <font-awesome-icon :icon="'fa-regular fa-image-slash'" />
              </div>
            </div>
            <div class="brand-name">
              <div class="brand-name--descriptor">
                <span>{{ currentCompany.name }}</span>
              </div>
            </div>
          </div>

          <div v-for="brand in availableBrands" :key="'brand_' + brand.id" @click="selectBrand(brand.id)" class="brand" :class="{ selected: selectedId === brand.id }">
            <div class="brand-logo">
              <img v-if="brand.brand_logo" :src="brand.brand_logo" :alt="brand.name">
              <div v-else class="no-logo">
                <font-awesome-icon :icon="'fa-regular fa-image-slash'" />
              </div>
            </div>
            <div class="brand-name">
              <div class="brand-name--descriptor">
                <span>{{ brand.name }}</span>
              </div>
            </div>
          </div>

        </div>
      </overlay-scrollbars>
      <GekkoButton
        @click="addBrand"
        :text="$gettext('Add trade name')"
        :icon="'fa-solid fa-plus'"
        variant="link"
        size="sm"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import GekkoButton from "@/components/GekkoButton"

export default {
  name: 'BrandSelector',
  components: { 
    GekkoButton
  },
  props: {
    preselect: {
      type: Object,
      default: null
    },
    emitObject: {
      type: Boolean,
      default: false
    },
    hideOnSelect: {
      type: Boolean,
      default: true
    },
  },
  data () {
    return {
      openSelector: false,
      selectedId: null,
      loading: true,
      scrollBarOptions: {
        scrollbars: { 
          autoHide: "never",
          dragScrolling: true,
          clickScrolling: true,
          touchSupport: true,
          snapHandle: true
        }, 
        overflowBehavior: { y: 'scroll', x: 'scroll' },
      }
    }
  },
  computed: {
    ...mapGetters ({
      currentCompany: "main/getCurrentCompany",
      companyBrands: "brands/getAvailableBrands",
      brandById: "brands/getBrandById",
      hasMainBrand: "brands/hasMainBrand"
    }),
    availableBrands () {
      // Filter out archived brands
      return this.companyBrands.filter(brand => !brand.is_archived)
    },
    selectedBrand () {
      const selectedBrand = this.availableBrands.find(brand => brand.id === this.selectedId)
      return (selectedBrand) ? selectedBrand : null
    },
    cssVars () {
      let sel_items = this.availableBrands.length
      if (!this.hasMainBrand) sel_items++
      return {
        '--sel-items': sel_items,
      }
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.addEventListener('click', this.close)
  },
  beforeDestroy () {
    document.removeEventListener('click',this.close)
  },
  methods: {
    ...mapActions ({
      loadBrandsMainData: 'brands/loadBrandsMainData'
    }),
    setupData () {
      this.loading = true
      this.loadBrandsMainData(this.currentCompany.id).then(() => {
        this.$nextTick(() => {
          this.loading = false
          if (this.preselect) {
            this.selectedId = this.preselect.id;
          }
        })
      })
    },
    toggleDropdown () {
      if (this.currentCompany.is_premium) {
        this.openSelector = !this.openSelector
      } else {
        this.$store.commit('togglePremiumModal', 'web_brands')
      }
    },
    addBrand () {
      if (this.currentCompany.is_premium) {
        this.$emit('add-brand')
      } else {
        this.$store.commit('togglePremiumModal', 'web_brands')
      }
    },
    selectMainBrand () {
      this.selectedId = null
      this.openSelector = !this.openSelector
      this.$emit('change', null)
    },
    selectBrand (brand_id) {
      if (this.currentCompany.is_premium) {
        if (this.selectedId === brand_id) return false
        this.selectedId = brand_id
        this.openSelector = !this.openSelector
        let emit_data
        if (this.emitObject) {
          emit_data = this.brandById(brand_id)
        } else {
          emit_data = brand_id
        }
        this.$emit('change', emit_data)
      } else {
        this.$store.commit('togglePremiumModal', 'web_brands')
      }
    },
    close (e) {
      if (!this.$el.contains(e.target)) {
        this.openSelector = false
      }
    }
  }
}
</script>
<style lang="scss">
  .brand-selector {
    .options-list-wrap {
      border: 1px solid transparent;
      border-radius: $radiusLg;

      .os-host { 
        height: calc(100% - 30px) !important; // add new button height
        // border: 1px solid rgba(0, 0, 0, 0.15);
        // border-radius: $radiusLg;
        .os-content { padding: 0 !important; }
        &.os-host-overflow-y {
          .os-content { padding-right: 0.5rem !important; }
        }
      }
    }
  }
</style>
<style lang="scss" scoped>
  .brand-selector {
    position: relative;
    &.loading { overflow: hidden; }
    .selector-caret {
      font-size: 12px;
      color: $colorBrand;
      margin-left: 0.25rem;
    }
    .brand {
      display: flex;
      align-items: center;
      border-radius: $radiusSm;
      .brand-logo {
        border-radius: 100px;
        overflow: hidden;
        display: flex;
        height: 100%;
        aspect-ratio: 1;
        width: 34px;
        margin-right: 0.5rem;
        flex-shrink: 0;
        img { width: 100%; }

        background: #f2f2f2;

        display: flex;
        justify-content: center;
        align-items: center;
        .svg-inline--fa {
          font-size: 1rem;
          opacity: 0.3;
        }
        img { 
          object-fit: cover;
          width: 100%;
          height: 100%;
        }
        .no-logo {
          padding: 5px;
          text-align: center;
          line-height: 1rem;
          font-size: 14px;
        }
      }
      .brand-name {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        white-space: nowrap;
        &--descriptor {
          line-height: 1;
          white-space: nowrap;
          font-size: 16px;
        }
        span { 
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
    .selected-brand {
      height: 100%;
      cursor: pointer;
      &.none-selected {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        font-size: 14px;
        color: $colorBrand;
      }
      .brand-logo {
        height: 100%;
        aspect-ratio: 1;
        width: 48px;
      }
      .brand-name {
        padding-right: 1.5rem;
        &--descriptor {
          font-size: 18px;
        }
      }
    }
    &.open-selector {
      .options-list-wrap {
        $baseItemHeight: 50px;
        $itemHeight: calc(#{$baseItemHeight} + 0.5rem);
        $selHeight: calc(#{$itemHeight} * var(--sel-items));
        height: calc(#{$selHeight} + 40px); // border size 10px + add new button
        border: 1px solid rgba(0, 0, 0, 0.15);
        box-shadow: $dropdownShadow;
      }
    }
    .options-list-wrap {
      z-index: 100;
      position: absolute;
      top: calc(100% + 0.25rem);
      left: 0;
      height: 0;
      width: 100%;
      max-height: 300px;
      max-width: 300px;
      transition: height 0.2s ease-in-out;
      overflow: hidden;
      border-radius: $radiusLg;
      background: white;
      text-align: center;
      .options-list {
        padding: 0.5rem;
        width: 100%;
        .brand {
          padding: 0.5rem;
          transition: all 0.2s ease;
          cursor: pointer;
          &.selected {
            opacity: 0.3;
            background-color: $colorUnassigned200;
            &:hover { background-color: $colorUnassigned200; }
          }
          &:hover {
            background: rgba(34, 172, 138, 0.07);
            color: #06926f;
          }
          &:not(:last-child) {
            margin-bottom: 0.5rem;
          }
        }
      }
      
    }
  }
</style>
